// @ts-nocheck
import { forwardRef, useMemo } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Button,
  Badge,
  Progress,
  ThemeIcon,
  ActionIcon,
  useMantineTheme,
} from '@mantine/core';
import {
  IconSparkles,
  IconX,
  IconTrendingUp,
  IconAlertTriangle,
  IconBulb,
  IconChartBar,
} from '@tabler/icons-react';
import { Paper } from '../../../ui';
import { FadeIn, HoverLift } from '../../../animations';
import { getContrastingTextColor } from '../../../../utils/color';
import type { AiInsightCardProps } from '../types';

/**
 * AiInsightCard - AI-generated insight display
 *
 * Displays AI-generated insights with confidence scores and action buttons.
 */
const AiInsightCard = forwardRef<HTMLDivElement, AiInsightCardProps>(
  (
    {
      insight,
      variant = 'default',
      showConfidence = true,
      showTimestamp = true,
      showCategory = true,
      onDismiss,
      className,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const gradient = theme.other?.gradients?.info;

    const gradientBg = `linear-gradient(135deg, ${gradient?.from}, ${gradient?.to})`;
    const autoTextColor = useMemo(
      () => getContrastingTextColor(gradientBg),
      [gradientBg]
    );

    const categoryConfig = {
      trend: {
        icon: IconTrendingUp,
        color: 'blue',
        label: 'Trend Analysis',
      },
      anomaly: {
        icon: IconAlertTriangle,
        color: 'orange',
        label: 'Anomaly Detected',
      },
      recommendation: {
        icon: IconBulb,
        color: 'violet',
        label: 'Recommendation',
      },
      forecast: {
        icon: IconChartBar,
        color: 'teal',
        label: 'Forecast',
      },
      optimization: {
        icon: IconSparkles,
        color: 'cyan',
        label: 'Optimization',
      },
      risk: {
        icon: IconAlertTriangle,
        color: 'red',
        label: 'Risk Alert',
      },
    };

    const config = categoryConfig[insight.category] || categoryConfig.recommendation;
    const Icon = config.icon;

    const getRelativeTime = (date: Date) => {
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${diffDays}d ago`;
    };

    const isCompact = variant === 'compact';
    const isDetailed = variant === 'detailed';

    // Parse markdown-style content (simple implementation)
    const renderContent = (content: string) => {
      // Bold: **text**
      const withBold = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      // Lists: - item
      const withLists = withBold.replace(/^- (.+)$/gm, '• $1');
      return withLists;
    };

    return (
      <FadeIn>
        <HoverLift>
          <Paper
            ref={ref}
            variant="gradient"
            gradientVariant="info"
            p={isCompact ? 'md' : 'lg'}
            className={className}
            style={{
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* AI Sparkle decoration */}
            <Box
              style={{
                position: 'absolute',
                top: -20,
                right: -20,
                opacity: 0.1,
              }}
            >
              <IconSparkles size={120} color={autoTextColor} />
            </Box>

            <Stack gap={isCompact ? 'sm' : 'md'} style={{ position: 'relative' }}>
              {/* Header */}
              <Group justify="space-between" align="flex-start">
                <Group gap="sm">
                  <ThemeIcon
                    size={isCompact ? 'md' : 'lg'}
                    radius="md"
                    variant="transparent"
                    style={{
                      background: 'rgba(255,255,255,0.2)',
                      color: autoTextColor,
                    }}
                  >
                    <Icon size={isCompact ? 16 : 20} />
                  </ThemeIcon>

                  <Stack gap={4}>
                    {showCategory && (
                      <Badge
                        variant="white"
                        color="white"
                        size="sm"
                        style={{ color: theme.colors[config.color][6] }}
                      >
                        {config.label}
                      </Badge>
                    )}

                    {showTimestamp && (
                      <Text size="xs" c={autoTextColor} style={{ opacity: 0.8 }}>
                        {getRelativeTime(insight.timestamp)}
                      </Text>
                    )}
                  </Stack>
                </Group>

                {onDismiss && (
                  <ActionIcon
                    variant="white"
                    color="white"
                    onClick={() => onDismiss(insight.id)}
                    size="sm"
                  >
                    <IconX size={14} />
                  </ActionIcon>
                )}
              </Group>

              {/* Title */}
              <Text size={isCompact ? 'md' : 'lg'} fw={700} c={autoTextColor}>
                {insight.title}
              </Text>

              {/* Content */}
              <Text
                size={isCompact ? 'xs' : 'sm'}
                c={autoTextColor}
                style={{ opacity: 0.95, lineHeight: 1.6 }}
                dangerouslySetInnerHTML={{ __html: renderContent(insight.content) }}
              />

              {/* Confidence */}
              {showConfidence && insight.confidence !== undefined && (
                <Box>
                  <Group justify="space-between" mb={4}>
                    <Text size="xs" c={autoTextColor} fw={500} style={{ opacity: 0.9 }}>
                      Confidence
                    </Text>
                    <Text size="xs" c={autoTextColor} fw={600}>
                      {(insight.confidence * 100).toFixed(0)}%
                    </Text>
                  </Group>
                  <Progress
                    value={insight.confidence * 100}
                    color="white"
                    size="sm"
                  />
                </Box>
              )}

              {/* Actions */}
              {insight.actions && insight.actions.length > 0 && (
                <Group gap="sm">
                  {insight.actions.map((action, idx) => (
                    <Button
                      key={idx}
                      variant={action.variant === 'primary' ? 'white' : 'subtle'}
                      color="white"
                      onClick={action.onClick}
                      leftSection={action.icon}
                      size={isCompact ? 'xs' : 'sm'}
                      style={
                        action.variant === 'primary'
                          ? { color: theme.colors[config.color][6] }
                          : { color: autoTextColor }
                      }
                    >
                      {action.label}
                    </Button>
                  ))}
                </Group>
              )}
            </Stack>
          </Paper>
        </HoverLift>
      </FadeIn>
    );
  }
);

AiInsightCard.displayName = 'AiInsightCard';

export default AiInsightCard;
